import { FC, useMemo, useState } from "react";
import { Box, Stack } from "@mui/material";
import { TSharedCaseData } from "@/features/sharing/types/shared-case";
import { CaseTitle } from "@/features/case/components/CaseTitle";
import { SharedCaseBasicInfo } from "@/features/sharing/components/SharedCaseBasicInfo";
import { CaseEditMatches } from "@/features/case/components/CaseEdit/CaseEditMatches";
import { SharedCaseSchedules } from "@/features/sharing/components/SharedCaseSchedules";
import { TGetJobDto } from "@/api/services/main/job/dtos/get-job.dto";
import { TCaseMatches } from "@/api/services/main/case/dtos/get-case.dto";
import { TCaseScheduleItem } from "@/features/case/types/case";
import { useSharedCaseMatches } from "@/api/services/main/sharing";

type SharedCaseDetailProps = {
  data: TSharedCaseData;
};

const PAGE_SIZE_OPTIONS = [50, 100, 200] as const;

export const SharedCaseDetail: FC<SharedCaseDetailProps> = ({ data }) => {
  const jobInfo = (data.job ?? {}) as TGetJobDto;
  const shareUrl = data.shareUrl ?? undefined;

  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_OPTIONS[0]);
  const [currentPage, setCurrentPage] = useState(1);
  // Default sort by id DESC to show newest case matches first
  const [currentSort, setCurrentSort] = useState<string>("id|DESC");

  // Server-side paginated & sorted case matches
  const { data: paginatedResponse } = useSharedCaseMatches(shareUrl, {
    page: currentPage,
    perPage: pageSize,
    sorts: currentSort || undefined,
  });

  // Use API paginated data when available, fall back to verify response data
  const hasPaginatedApi = !!paginatedResponse?.data;

  const paginatedCaseMatches = useMemo(() => {
    const source = hasPaginatedApi ? paginatedResponse.data : (data.caseMatches ?? []);
    return source.map((cm) => ({
      ...cm,
      caseMatchUser: cm.caseMatchUser ?? data.personInCharge,
    })) as unknown as TCaseMatches[];
  }, [hasPaginatedApi, paginatedResponse?.data, data.caseMatches, data.personInCharge]);

  const total = paginatedResponse?.pagination?.total ?? (data.caseMatches ?? []).length;
  const totalPage = paginatedResponse?.pagination?.totalPage ?? Math.max(1, Math.ceil(total / pageSize));
  const countItem = paginatedResponse?.pagination?.count ?? paginatedCaseMatches.length;

  // All case matches for CaseTitle stats (from verify response)
  const allCaseMatchesForTitle = useMemo(
    () =>
      (data.caseMatches ?? []).map((cm) => ({
        ...cm,
        caseMatchUser: cm.caseMatchUser ?? data.personInCharge,
      })) as unknown as TCaseMatches[],
    [data.caseMatches, data.personInCharge],
  );
  const totalCandidates = (data.caseMatches ?? []).length;

  const formatTotal = (t: number) => t.toString().padStart(2, "0");

  // Build talent lookup from caseMatches to enrich schedule talents
  const talentMap = useMemo(() => {
    const map = new Map<number, { id: number; fullName: string }>();
    (data.caseMatches ?? []).forEach((cm) => {
      if (cm.talent?.id) {
        map.set(cm.talent.id, { id: cm.talent.id, fullName: cm.talent.fullName });
      }
    });
    return map;
  }, [data.caseMatches]);

  // Enrich schedules: resolve talentIds to full talent objects using caseMatches data
  const enrichedSchedules = useMemo<TCaseScheduleItem[]>(() => {
    return (data.caseSchedules ?? []).map((schedule) => {
      // If talents already populated with data, use as-is
      if (schedule.talents && schedule.talents.length > 0) return schedule;
      // Try resolving from talentIds using talent lookup map
      const raw = schedule as TCaseScheduleItem & {
        talentIds?: number[];
        caseTalents?: { talentId: number }[];
        caseScheduleTalents?: { talentId: number }[];
      };
      const talentIds = raw.talentIds ?? raw.caseScheduleTalents?.map((ct) => ct.talentId) ?? raw.caseTalents?.map((ct) => ct.talentId) ?? [];
      if (talentIds.length === 0) return schedule;
      const resolvedTalents = talentIds.map((id) => talentMap.get(id)).filter((t): t is { id: number; fullName: string } => !!t);
      return { ...schedule, talents: resolvedTalents };
    });
  }, [data.caseSchedules, talentMap]);

  return (
    <Stack direction="row" justifyContent="center">
      <Box width={1134} mt={4}>
        <CaseTitle
          jobInfo={jobInfo}
          managementNumber={data.managementNumber ?? undefined}
          caseName={data.name}
          progressStatus={data.progressStatus}
          caseMatches={allCaseMatchesForTitle}
          totalCandidates={totalCandidates}
          isAllFailed={data.isAllFailed}
          shareUrl={shareUrl}
          sharePassword={data.sharePassword ?? undefined}
          isSharedView={true}
        />
        <Stack direction="row" justifyContent="space-between">
          <Box width={752}>
            <SharedCaseBasicInfo
              jobInfo={jobInfo}
              name={data.name}
              managementNumber={data.managementNumber}
              personInChargeName={data.personInCharge?.fullName}
            />
            <CaseEditMatches
              caseMatchesData={paginatedCaseMatches}
              total={total}
              countItem={countItem}
              totalPage={totalPage}
              formatTotal={formatTotal}
              canEdit={false}
              isCompleted={true}
              caseName={data.name}
              isSharedView={true}
              shareUrl={shareUrl}
              sharedPageSize={pageSize}
              sharedPageSizeOptions={PAGE_SIZE_OPTIONS as unknown as number[]}
              onSharedPageSizeChange={(size) => {
                setPageSize(size);
                setCurrentPage(1);
              }}
              sharedCurrentPage={currentPage}
              onSharedPageChange={setCurrentPage}
              currentSort={currentSort}
              onSort={(sort) => {
                setCurrentSort(sort);
                setCurrentPage(1);
              }}
            />
          </Box>
          <Box width={350}>
            <SharedCaseSchedules caseSchedules={enrichedSchedules} />
          </Box>
        </Stack>
      </Box>
    </Stack>
  );
};
